import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiFetch } from '../../utils/apiConfig';
import {
  Typography,
  Box,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemText,
  Menu,
  ListItemIcon
} from '@mui/material';
import {
  DragIndicator,
  AccessTime,
  Search,
  Star,
  StarBorder,
  Delete,
  MergeType
} from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createApiUrl } from '../../utils/apiConfig';
import { getAuthHeaders, getAuthToken } from '../../utils/authUtils';
import { formatDateTime, formatDateTimeSync, formatDateSync, formatDateWithAgeSync, loadFormatConfig, useFormatConfig } from '../../utils/dateTimeFormat';
import ColumnOrderDialog from './ColumnOrderDialog';
import DraggableColumnHeader, { reorderColumnFields } from './DraggableColumnHeader';
import { InsuranceStatusChip, EmergencyStatusChip } from './editor/StatusBadges';
import TaskSearchBar from './filters/TaskSearchBar';
import TaskFilterDialog from './filters/TaskFilterDialog';
import { useTaskQueryFilters } from '../../hooks/useTaskQueryFilters';
import { useSnackbar } from '../CentralSnackbar';
import { useWebSocket } from '../../contexts/WebSocketContext';

// Spalten, die aus der Konfiguration ausgeschlossen werden sollen
const EXCLUDED_COLUMNS = [
  'created_at', 'updated_at', 'id', 'report_uid', 'new_exam_id', 'new_report_id',
  'template_id', 'author_id', 'approved_by', 'rejected_by', 'isApproved', 'isRejected', 'isEditing',
  'status', // Leeres Status-Feld (Untersuchungsstatus) - verwende stattdessen report_status
  'resultStatus', // PACS Befundstatus-Feld ausblenden - verwende stattdessen report_status
  'first_reader', // User-ID ausblenden - verwende stattdessen first_reader_name
  'current_reader' // User-ID ausblenden - verwende stattdessen current_reader_name
];

// Felder, die nur als Datum formatiert werden sollen (ohne Zeit)
// Beide camelCase und snake_case Varianten berücksichtigen
const DATE_FIELDS = [
  'birth_date', 'study_date', 'studyDate', 'studydate',
  'archiveDate', 'archivedate'
];

// Felder, die als Datum/Zeit formatiert werden sollen
const DATETIME_FIELDS = [
  'study_time', 'studyTime', 'studytime', 'performed_procedure_step_start_date', 'performed_procedure_step_start_time', 
  'timeOfStatusChange', 'observation_date', 'approval_time', 'rejection_time', 'time_of_status_change',
  'created_at', 'updated_at'
];

// Grid height configuration (same as MyTasks)
const HEADER_HEIGHT = 56;
const ROW_HEIGHT = 52;
const GRID_MAX_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT * 12;
/** Dashboard-Widget: `zoom` statt `transform: scale` (vermeidet Leerraum unter der Grid). */
const WIDGET_TABLE_ZOOM = 0.7;
const SHOW_COLUMN_ORDER_DIALOG = false;

// Hilfsfunktion für Status-zu-CSS-Klasse Mapping - SPRACHUNABHÄNGIG
const getStatusCssClass = (status) => {
  if (!status) return 'status-new';
  
  // Normalisierung: Case-insensitive Matching
  const normalizedStatus = status.toString().toLowerCase().trim();
  
  // AUSSCHLIESSLICH Backend ReportStatus Enum Werte - CSS-Klassen entsprechen exakt Backend-Namen
  const statusMap = {
    // Diese 5 Werte stehen IMMER in der Datenbank (Backend Enum):
    'new': 'status-new',                    // ReportStatus.NEW = "NEW"
    'started': 'status-started',            // ReportStatus.STARTED = "STARTED"  
    'provisionally': 'status-provisionally', // ReportStatus.PROVISIONALLY = "PROVISIONALLY"
    'awaiting_approval': 'status-awaiting-approval', // ReportStatus.AWAITING_APPROVAL = "AWAITING_APPROVAL"
    'approved': 'status-approved'           // ReportStatus.APPROVED = "APPROVED"
  };
  
  return statusMap[normalizedStatus] || 'status-new';
};

// Gruppierungsfunktion für kombinierte Befunde
// Erstellt übergeordnete Einträge und markiert Kind-Einträge
const groupCombinedReports = (data) => {
  const result = [];
  const processedCombinedReportIds = new Set();
  
  // Sortiere zuerst nach combined_report_id um zusammengehörige zu gruppieren
  const sortedData = [...data].sort((a, b) => {
    const aId = a.combined_report_id || 0;
    const bId = b.combined_report_id || 0;
    return bId - aId; // Kombinierte zuerst
  });
  
  for (const exam of sortedData) {
    // Prüfe ob dieser Eintrag Teil eines kombinierten Befunds ist
    if (exam.is_combined && exam.combined_report_id) {
      // Überspringe, wenn wir diese Gruppe bereits verarbeitet haben
      if (processedCombinedReportIds.has(exam.combined_report_id)) {
        continue;
      }
      
      // Finde alle Einträge für diesen kombinierten Befund
      const groupMembers = sortedData.filter(e => 
        e.combined_report_id === exam.combined_report_id
      );
      
      if (groupMembers.length > 1) {
        // Erstelle übergeordneten Gruppen-Eintrag
        const parentEntry = {
          ...groupMembers[0], // Basis-Daten vom ersten Mitglied
          id: `combined-${exam.combined_report_id}`, // Eindeutige ID für den Gruppen-Eintrag
          _isGroupParent: true,
          _groupChildren: groupMembers.map(m => m.id),
          _combinedReportId: exam.combined_report_id,
          // Kombinierte Studienbeschreibung
          study_description: groupMembers.map(m => m.study_description).join('; '),
          // Kombinierte Modalitäten
          modality: [...new Set(groupMembers.map(m => m.modality))].join('; '),
          // Zeige den current_reader_name vom ersten Mitglied (alle sollten gleich sein)
          current_reader_name: groupMembers[0].current_reader_name,
          // Status vom ersten Mitglied (alle sollten gleich sein)
          report_status: groupMembers[0].report_status
        };
        
        result.push(parentEntry);
        
        // Füge Kind-Einträge hinzu (eingerückt, nicht klickbar)
        for (const member of groupMembers) {
          result.push({
            ...member,
            _isGroupChild: true,
            _parentId: `combined-${exam.combined_report_id}`,
            _combinedReportId: exam.combined_report_id
          });
        }
        
        processedCombinedReportIds.add(exam.combined_report_id);
      } else {
        // Nur ein Eintrag, behandle wie normalen Eintrag
        result.push(exam);
        processedCombinedReportIds.add(exam.combined_report_id);
      }
    } else {
      // Normaler einzelner Befund
      result.push(exam);
    }
  }
  
  console.log(`TeamTasks: ${result.length} Einträge nach Gruppierung (${processedCombinedReportIds.size} kombinierte Gruppen)`);
  return result;
};

const TEAM_TASKS_PAGE_CONTENT_ZOOM = 0.8;

const TeamTasks = ({ embedAsWidget = false, widgetBodyHeight = null }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const effectiveGridHeight = useMemo(() => {
    if (embedAsWidget && widgetBodyHeight != null && Number.isFinite(widgetBodyHeight)) {
      const pad = 28;
      return Math.max(48, Math.floor(widgetBodyHeight) - pad);
    }
    return GRID_MAX_HEIGHT;
  }, [embedAsWidget, widgetBodyHeight]);
  const { isLoaded: formatConfigLoaded } = useFormatConfig();
  const { showSuccess, showError, showWarning } = useSnackbar();
  const { newMessage } = useWebSocket();
  const [teamExams, setTeamExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userChecked, setUserChecked] = useState(false);
  const [columns, setColumns] = useState([]);
  const [taskSettings, setTaskSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const saveTimeout = useRef(null);
  const {
    searchChips,
    addSearchChip,
    removeSearchChip,
    conditions,
    setConditions,
    validConditions,
    hasActiveConditions,
    clearConditions,
    removeCondition,
    applySavedFilter,
    applyToRows,
    filterDialogOpen,
    filterAnchorEl,
    openFilterDialog,
    closeFilterDialog
  } = useTaskQueryFilters();
  const [modalityFilter, setModalityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [studyDateFilter, setStudyDateFilter] = useState('');
  const [availableModalities, setAvailableModalities] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  
  // Alle möglichen ReportStatus (Backend Enum)
  const ALL_REPORT_STATUSES = ['NEW', 'STARTED', 'PROVISIONALLY', 'AWAITING_APPROVAL', 'APPROVED'];
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showColumnOrderDialog, setShowColumnOrderDialog] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [dialogColumnVisibilityModel, setDialogColumnVisibilityModel] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [lockedReports, setLockedReports] = useState({}); // { report_id: { opened_by, user_name } }
  
  // Gespeicherte Filter
  const [savedFilters, setSavedFilters] = useState([]);
  const [defaultFilterId, setDefaultFilterId] = useState(null);
  const [selectedFilterId, setSelectedFilterId] = useState('');
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  
  // Multi-Selection für zusammengefasste Befunde
  const [selectionModel, setSelectionModel] = useState([]);
  const [contextMenu, setContextMenu] = useState(null); // { mouseX, mouseY }
  const [combineValidating, setCombineValidating] = useState(false);



  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(createApiUrl('auth/me'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        setUserChecked(true);
      } else {
        showError(t('teamTasks.error.userDataLoad'), 'ERR-TEAMTASKS-001');
        setUserChecked(true); // Trotzdem als gecheckt markieren
      }
    } catch (err) {
      showError(t('teamTasks.error.networkUserData'), 'ERR-TEAMTASKS-002');
      setUserChecked(true); // Trotzdem als gecheckt markieren
    }
  };

  const fetchColumns = async () => {
    try {
      const response = await fetch(createApiUrl('profile/task-columns'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Filter direkt anwenden
        const filtered = (data.columns || []).filter(col => !EXCLUDED_COLUMNS.includes(col));
        setColumns(filtered);
      }
    } catch (err) {
      setColumns([]);
    }
  };

  const fetchTaskSettings = async () => {
    try {
      const response = await fetch(createApiUrl('profile/task-settings'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTaskSettings(data.task_settings || null);
      }
    } catch (err) {
      setTaskSettings(null);
    }
  };

  // Debounce-Speichern
  const debounceSave = (newSettings) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      saveTaskSettings(newSettings);
    }, 500);
  };

  // Initial loading effect - lädt Task Settings und Spalten-Konfiguration
  useEffect(() => {
    const initializeComponent = async () => {
      // Lade Format Config für Datumsformatierung
      await loadFormatConfig();
      
      // Lade verfügbare Spalten-Konfiguration
      try {
        const response = await fetch(createApiUrl('profile/task-columns'), {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setColumns(data.columns || []);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Spalten-Konfiguration:', error);
        showError(t('teamTasks.error.columnsLoad'), 'ERR-TEAMTASKS-003');
      }

      // Lade Task Settings
      await loadTaskSettings();
      
      // Lade alle Modalitäten
      await fetchAllModalities();
    };

    initializeComponent();
  }, []);
  
  // Lade alle Modalitäten aus der Datenbank
  const fetchAllModalities = async () => {
    try {
      const response = await fetch(createApiUrl('examinations/modalities'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableModalities(data.modalities || []);
      }
    } catch (err) {
      console.error('Error fetching modalities:', err);
    }
  };
  
  // Status-Liste wird aus der Konstante verwendet
  useEffect(() => {
    setAvailableStatuses(ALL_REPORT_STATUSES);
  }, []);

  // ENTFERNT: extractAvailableModalities und extractAvailableStatuses
  // Modalitäten werden jetzt aus der Datenbank geladen
  // Status werden aus der ALL_REPORT_STATUSES Konstante verwendet

  const loadTeamExams = async () => {
    setLoading(true);
    
    try {
      // URL-Parameter für Filter erstellen
      const params = new URLSearchParams();
      if (modalityFilter) params.append('modality', modalityFilter);
      if (studyDateFilter) params.append('study_date', studyDateFilter);
      // Status-Filter wird client-seitig angewendet, da der Server alle nicht-approved Reports zurückgibt
      
      // Neue API verwenden für alle noch nicht approved Befunde
      const url = `all-pending-reports${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(createApiUrl(url), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        let data = await response.json();
        
        // Zusätzlicher Sicherheitsfilter: APPROVED/Freigegeben Befunde ausschließen
        data = data.filter(exam => {
          const reportStatus = exam.report_status;
          // Schließe APPROVED Befunde aus
          return reportStatus !== 'APPROVED' && reportStatus !== 'Approved' && reportStatus !== 'approved';
        });
        
        console.log(`TeamTasks: ${data.length} nicht-freigegebene Befunde geladen`);
        
        // Client-seitige Status-Filterung anwenden
        if (statusFilter) {
          data = data.filter(exam => exam.report_status === statusFilter);
        }
        
        // Gruppiere kombinierte Befunde und erstelle übergeordnete Einträge
        const groupedData = groupCombinedReports(data);
        
        setTeamExams(groupedData);
      } else {
        showError(t('teamTasks.error.loadingReports'), 'ERR-TEAMTASKS-004');
        setTeamExams([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der offenen Befunde:', err);
      showError(t('teamTasks.error.networkLoadingReports'), 'ERR-TEAMTASKS-005');
      setTeamExams([]);
    } finally {
      setLoading(false);
    }
  };

  // Task Settings laden
  const loadTaskSettings = async () => {
    try {
      const response = await fetch(createApiUrl('profile/task-settings'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📥 RAW task_settings from API:', data.task_settings);
        console.log('📥 Type of task_settings:', typeof data.task_settings);
        
        // Parse task_settings wenn es ein String ist
        let settings = data.task_settings;
        if (typeof settings === 'string') {
          try {
            settings = JSON.parse(settings);
            console.log('📥 Parsed task_settings:', settings);
          } catch (parseErr) {
            console.error('❌ Error parsing task_settings JSON:', parseErr);
            settings = null;
          }
        }
        
        console.log('📥 Final task_settings:', settings);
        setTaskSettings(settings || null);
      }
    } catch (err) {
      console.error('❌ Error fetching task settings:', err);
      setTaskSettings(null);
    }
  };

  // ========================================
  // Gespeicherte Filter - Funktionen
  // ========================================
  
  // Aktuellen Filter speichern
  const handleSaveFilter = async ({ name, conditions: conditionsToSave, setAsDefault = true }) => {
    const filterName = String(name || '').trim();
    if (!filterName) {
      showError(t('teamTasks.filters.nameRequired', 'Bitte geben Sie einen Filternamen ein'));
      return false;
    }

    if (!conditionsToSave || conditionsToSave.length === 0) {
      showError(t('taskFilters.noConditions'));
      return false;
    }
    
    // Auto-Save blockieren während wir speichern
    isLoadingFiltersRef.current = true;
    
    const newFilter = {
      id: `filter-${Date.now()}`,
      name: filterName,
      conditions: conditionsToSave
    };
    
    const updatedFilters = [...savedFilters, newFilter];
    setSavedFilters(updatedFilters);

    const nextDefaultId = setAsDefault ? newFilter.id : defaultFilterId;
    if (setAsDefault) {
      setDefaultFilterId(newFilter.id);
    }
    setSelectedFilterId(newFilter.id);
    
    // Update Refs um Race Conditions zu vermeiden
    loadedFiltersRef.current = JSON.stringify(updatedFilters);
    loadedDefaultIdRef.current = nextDefaultId;
    
    // In taskSettings speichern ohne andere Felder zu überschreiben
    const newSettings = {
      ...taskSettings,
      savedFilters: updatedFilters,
      defaultFilterId: nextDefaultId,
      filters: {
        modality: modalityFilter,
        status: statusFilter,
        studyDate: studyDateFilter
      }
    };
    
    await saveTaskSettings(newSettings);
    
    // Auto-Save wieder erlauben
    setTimeout(() => {
      isLoadingFiltersRef.current = false;
    }, 100);
    
    showSuccess(setAsDefault
      ? t('teamTasks.filters.saved', 'Filter gespeichert und als Standard gesetzt')
      : t('taskFilters.saved', 'Filter gespeichert'));
    return true;
  };
  
  // Gespeicherten Filter laden
  const loadFilter = (filter) => {
    applySavedFilter(filter);
    setSelectedFilterId(filter.id);
  };
  
  // Filter als Standard setzen/entfernen
  const toggleDefaultFilter = async (filterId) => {
    const newDefaultId = defaultFilterId === filterId ? null : filterId;
    
    // WICHTIG: Auto-Save blockieren während wir speichern
    isLoadingFiltersRef.current = true;
    
    setDefaultFilterId(newDefaultId);
    
    // Update Refs SOFORT um Race Conditions zu vermeiden
    loadedDefaultIdRef.current = newDefaultId;
    
    if (newDefaultId) {
      // Stern aktiviert: Filter als Default setzen und SOFORT anwenden
      const filter = savedFilters.find(f => f.id === newDefaultId);
      if (filter) {
        console.log('⭐ Setting as default and applying filter:', filter);
        loadFilter(filter);
      }
    } else {
      // Stern deaktiviert: Filter zurücksetzen (leeren)
      console.log('☆ Removing default filter, resetting filter values');
      setModalityFilter('');
      setStatusFilter('');
      setStudyDateFilter('');
      setSelectedFilterId('');
      clearConditions();
    }
    
    const newSettings = {
      ...taskSettings,
      savedFilters: savedFilters,
      defaultFilterId: newDefaultId,
      filters: newDefaultId ? {
        modality: savedFilters.find(f => f.id === newDefaultId)?.modality || '',
        status: savedFilters.find(f => f.id === newDefaultId)?.status || '',
        studyDate: savedFilters.find(f => f.id === newDefaultId)?.studyDate || ''
      } : {
        modality: '',
        status: '',
        studyDate: ''
      }
    };
    
    // WICHTIG: await verwenden um sicherzustellen dass gespeichert wird
    await saveTaskSettings(newSettings);
    
    // Auto-Save wieder erlauben NACH dem Speichern
    setTimeout(() => {
      isLoadingFiltersRef.current = false;
    }, 100);
  };
  
  // Gespeicherten Filter auswählen
  const handleFilterSelect = (event) => {
    const filterId = event.target.value;
    
    if (!filterId) {
      // "Keine Auswahl" - zurücksetzen
      setModalityFilter('');
      setStatusFilter('');
      setStudyDateFilter('');
      setSelectedFilterId('');
      clearConditions();
      return;
    }
    
    const filter = savedFilters.find(f => f.id === filterId);
    if (filter) {
      loadFilter(filter);
    }
  };
  
  // Gespeicherten Filter löschen
  const handleDeleteFilter = async (filterId) => {
    // Auto-Save blockieren während wir löschen
    isLoadingFiltersRef.current = true;
    
    const updatedFilters = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updatedFilters);
    
    // Wenn der gelöschte Filter der Default war, auf null zurücksetzen
    let newDefaultId = defaultFilterId;
    if (defaultFilterId === filterId) {
      newDefaultId = null;
      setDefaultFilterId(null);
    }
    
    // Wenn der gelöschte Filter aktuell ausgewählt war, zurücksetzen
    if (selectedFilterId === filterId) {
      setSelectedFilterId('');
      setModalityFilter('');
      setStatusFilter('');
      setStudyDateFilter('');
      clearConditions();
    }
    
    // Update Refs um Race Conditions zu vermeiden
    loadedFiltersRef.current = JSON.stringify(updatedFilters);
    loadedDefaultIdRef.current = newDefaultId;
    
    const newSettings = {
      ...taskSettings,
      savedFilters: updatedFilters,
      defaultFilterId: newDefaultId,
      filters: (defaultFilterId === filterId) ? {
        modality: '',
        status: '',
        studyDate: ''
      } : taskSettings.filters
    };
    
    await saveTaskSettings(newSettings);
    
    // Auto-Save wieder erlauben
    setTimeout(() => {
      isLoadingFiltersRef.current = false;
    }, 100);
    
    showSuccess(t('teamTasks.filters.deleted', 'Filter gelöscht'));
  };

  const saveTaskSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      const response = await fetch(createApiUrl('profile/task-settings'), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ task_settings: newSettings })
      });
      
      if (response.ok) {
        setTaskSettings(newSettings);
      } else {
        showError(t('teamTasks.error.savingSettings'), 'ERR-TEAMTASKS-006');
      }
    } catch (error) {
      console.error('Error saving task settings:', error);
      showError(t('teamTasks.error.networkSavingSettings'), 'ERR-TEAMTASKS-007');
    } finally {
      setSavingSettings(false);
    }
  };

  // PACS-Daten laden
  const fetchPacsData = async (accessionNumbers, requestedColumns) => {
    try {
      const response = await fetch(createApiUrl('pacs-data/batch'), {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accession_numbers: accessionNumbers,
          columns: requestedColumns
        })
      });

      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.error('Fehler beim Laden der PACS-Daten:', error);
      return [];
    }
  };

  // WebSocket-Listener für Report Lock/Unlock Events
  useEffect(() => {
    if (!newMessage) return;

    const { type, report_id, exam_id, opened_by, opened_by_name } = newMessage;
    
    if (type === 'report_opened' && report_id) {
      console.log(`[LOCK] Report ${report_id} geöffnet von ${opened_by_name}`);
      setLockedReports(prev => ({
        ...prev,
        [report_id]: { opened_by, user_name: opened_by_name }
      }));
    } else if (type === 'report_closed' && report_id) {
      console.log(`[LOCK] Report ${report_id} geschlossen`);
      setLockedReports(prev => {
        const updated = { ...prev };
        delete updated[report_id];
        return updated;
      });
    } else if (type === 'exam_opened' && exam_id) {
      console.log(`[LOCK] Exam ${exam_id} geöffnet von ${opened_by_name}`);
      setLockedReports(prev => ({
        ...prev,
        [exam_id]: { opened_by, user_name: opened_by_name }
      }));
    } else if (type === 'exam_closed' && exam_id) {
      console.log(`[LOCK] Exam ${exam_id} geschlossen`);
      setLockedReports(prev => {
        const updated = { ...prev };
        delete updated[exam_id];
        return updated;
      });
    }
  }, [newMessage]);

  // Settings aus DB laden und auf State anwenden (nur Spalten-Settings)
  useEffect(() => {
    if (taskSettings && taskSettings.columns) {
      // Sichtbarkeit
      const visibilityModel = {};
      taskSettings.columns.forEach(col => {
        visibilityModel[col.field] = col.visible !== false;
      });
      setColumnVisibilityModel(visibilityModel);
      
      // Breiten
      const widths = {};
      (taskSettings.columns || []).forEach(col => {
        if (col.width) widths[col.field] = col.width;
      });
      setColumnWidths(widths);
      
      // Reihenfolge - sortiere nach order property
      const sortedColumns = (taskSettings.columns || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      const order = sortedColumns.map(col => col.field);
      setColumnOrder(order);
    }
  }, [taskSettings, columns]);

  // Filter aus task_settings laden (separate useEffect um Endlosschleifen zu vermeiden)  
  const filtersLoadedRef = useRef(false);
  const isLoadingFiltersRef = useRef(false);
  
  useEffect(() => {
    if (taskSettings && !filtersLoadedRef.current) {
      // WICHTIG: Wenn ein defaultFilterId gesetzt ist, wird der Filter vom savedFilters-useEffect geladen
      // Wir laden hier NUR die letzten Filter-Werte wenn KEIN Default-Filter existiert
      if (taskSettings.defaultFilterId) {
        console.log('⏭️ Skipping filter restore from taskSettings.filters - will load default filter instead');
        // filtersLoadedRef wird vom savedFilters-useEffect gesetzt
        return;
      }
      
      // Verhindern, dass das Speicher-useEffect triggert während wir Filter laden
      isLoadingFiltersRef.current = true;
      
      if (taskSettings.filters) {
        console.log('🔄 Skipping last-used quick filters; use saved standard filters instead');
      } else {
        // Keine Filter gespeichert, auf leer setzen
        console.log('🔄 No filters in taskSettings, resetting to empty');
        setModalityFilter('');
        setStatusFilter('');
        setStudyDateFilter('');
      }
      
      // Nach dem Laden: Filter-Loading beenden, Speichern erlauben
      setTimeout(() => {
        isLoadingFiltersRef.current = false;
        filtersLoadedRef.current = true;
      }, 10);
    }
  }, [taskSettings]);

  // Ref um zu tracken ob savedFilters bereits geladen wurden
  const loadedFiltersRef = useRef(null);
  const loadedDefaultIdRef = useRef(null);
  
  // Gespeicherte Filter aus taskSettings laden und initialisieren
  useEffect(() => {
    if (!taskSettings) {
      return;
    }
    
    // Warten bis savedFilters vorhanden ist
    if (!taskSettings.savedFilters) {
      return;
    }
    
    // Prüfen ob sich savedFilters oder defaultFilterId geändert haben
    const filtersJson = JSON.stringify(taskSettings.savedFilters);
    const defaultId = taskSettings.defaultFilterId;
    
    const filtersChanged = loadedFiltersRef.current !== filtersJson;
    const defaultIdChanged = loadedDefaultIdRef.current !== defaultId;
    
    if (!filtersChanged && !defaultIdChanged) {
      // Keine Änderungen, nichts zu tun
      return;
    }
    
    console.log('🔍 FILTER INIT: Filters or defaultId changed', {
      filtersChanged,
      defaultIdChanged,
      defaultId
    });
    
    // Speichere die aktuellen Werte
    loadedFiltersRef.current = filtersJson;
    loadedDefaultIdRef.current = defaultId;
    
    // Lade gespeicherte Filter
    const filters = Array.isArray(taskSettings.savedFilters) ? taskSettings.savedFilters : [];
    setSavedFilters(filters);
    setDefaultFilterId(defaultId);
    
    // Wende Default-Filter an (nur beim ersten Laden)
    if (!isFiltersInitialized && defaultId) {
      const defaultFilter = filters.find(f => f.id === defaultId);
      
      if (defaultFilter) {
        console.log('✅ Loading default filter on init:', defaultFilter.name);
        isLoadingFiltersRef.current = true;

        applySavedFilter(defaultFilter);
        setSelectedFilterId(defaultFilter.id);
        
        setTimeout(() => {
          isLoadingFiltersRef.current = false;
          filtersLoadedRef.current = true;
        }, 100);
      }
      
      setIsFiltersInitialized(true);
    } else if (!isFiltersInitialized) {
      setIsFiltersInitialized(true);
    }
  }, [taskSettings, isFiltersInitialized, applySavedFilter]);

  // Filter automatisch speichern wenn sie sich ändern (aber nicht beim initialen Laden)
  useEffect(() => {
    if (taskSettings && filtersLoadedRef.current && !isLoadingFiltersRef.current) {
      // Nur Filter-Einstellungen speichern, NICHT die columns überschreiben
      const filterOnlyUpdate = {
        filters: {
          modality: modalityFilter,
          status: statusFilter,
          studyDate: studyDateFilter
        }
      };
      
      // API-Call nur für Filter-Update
      const saveFiltersOnly = async () => {
        try {
          const response = await fetch(createApiUrl('profile/task-settings'), {
            method: 'PUT',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              task_settings: {
                ...taskSettings,
                ...filterOnlyUpdate
              }
            })
          });
          
          if (response.ok) {
            // Filter sind bereits im State - kein setTaskSettings nötig!
            // Das würde eine Endlosschleife auslösen
          }
        } catch (error) {
          console.error('Error saving filters:', error);
        }
      };
      
      // Debounce das Speichern um excessive API-Aufrufe zu vermeiden
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
      saveTimeout.current = setTimeout(() => {
        saveFiltersOnly();
      }, 500);
    }
  }, [modalityFilter, statusFilter, studyDateFilter]);

  // Default-Konfiguration, falls User noch keine hat (same as MyTasks)
  const getDefaultColumnSettings = () => {
    return (columns || []).map((col, idx) => ({
      field: col,
      width: 150,
      order: idx,
      visible: true
    }));
  };

  // Die aktuelle Spaltenkonfiguration (User oder Default) (same as MyTasks)
  const userColumns = useMemo(() => {
    let result;
    if (taskSettings && taskSettings.columns) {
      result = taskSettings.columns.map(col => ({
        ...col,
        width: columnWidths[col.field] || col.width || 150
      }));
    } else {
      result = getDefaultColumnSettings().map(col => ({
        ...col,
        width: columnWidths[col.field] || col.width || 150
      }));
    }
    
    // Sortiere nach order property
    result.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    return result;
  }, [taskSettings, columnWidths]);

  const searchFields = useMemo(() => {
    const visible = userColumns
      .filter((col) => columnVisibilityModel[col.field] !== false)
      .map((col) => col.field);
    return visible.length > 0 ? visible : userColumns.map((col) => col.field);
  }, [userColumns, columnVisibilityModel]);

  const filterDialogColumns = useMemo(() => (
    userColumns.map((col) => ({
      field: col.field,
      label: t(`taskColumns.${col.field}`, col.field)
    }))
  ), [userColumns, t, i18n.language]);

  // Fallback für leere Settings (nur lokaler State, NICHT speichern!)
  useEffect(() => {
    if (!taskSettings && columns.length > 0) {
      // Default-Settings erstellen
      const defaultColumns = [
        'report_status',
        'accession_number',
        'patient_name',
        'patient_id',
        'study_description',
        'birth_date',
        'sex'
      ]
        .filter(col => columns.includes(col) || col === 'report_status' || col === 'first_reader_name')
        .map((col, index) => ({
          field: col,
          visible: true,
          order: index,
          width: 150
        }));

      const defaultSettings = {
        columns: defaultColumns,
        pageSize: 25
      };

      // Nur lokaler State, NICHT in Datenbank speichern!
      setTaskSettings(defaultSettings);
      // debounceSave(defaultSettings); <-- ENTFERNT: Das hat die DB überschrieben!
    }
  }, [columns, taskSettings]);

  // Initialisierung
  useEffect(() => {
    const init = async () => {
      // Lade Format-Konfiguration zuerst
      try {
        await loadFormatConfig();
      } catch (error) {
        console.warn('Could not load format configuration:', error);
      }
      
      await fetchCurrentUser();
      await fetchColumns();
      await fetchTaskSettings();
    };
    init();
  }, []);

  // Team exams laden wenn User verfügbar
  useEffect(() => {
    if (userChecked && currentUser) {
      loadTeamExams();
    }
  }, [userChecked, currentUser, modalityFilter, statusFilter, studyDateFilter]);

  // Modalitäten werden aus der Datenbank geladen, nicht aus teamExams extrahiert

  // Filter automatisch speichern wenn sie sich ändern (aber nicht beim initialen Laden)
  useEffect(() => {
    if (taskSettings && filtersLoadedRef.current && (modalityFilter || statusFilter || studyDateFilter)) {
      const updatedSettings = {
        ...taskSettings,
        filters: {
          modality: modalityFilter,
          status: statusFilter,
          studyDate: studyDateFilter
        }
      };
      debounceSave(updatedSettings);
    }
  }, [modalityFilter, statusFilter, studyDateFilter, taskSettings]);

  // PACS-Daten nachladen - optimiert
  useEffect(() => {
    if (teamExams.length > 0 && userColumns.length > 0) {
      const accessionNumbers = teamExams
        .map(exam => exam.accession_number)
        .filter(Boolean);

      if (accessionNumbers.length === 0) {
        return; // Keine AccessionNumbers vorhanden
      }

      const examFields = Object.keys(teamExams[0] || {});
      const pacsColumns = userColumns
        .map(col => col.field)
        .filter(field => !examFields.includes(field));

      if (pacsColumns.length === 0) {
        return; // Keine PACS-Spalten angefordert
      }

      const hasAllPacsData = teamExams.every(exam =>
        pacsColumns.every(col => exam[col] !== undefined && exam[col] !== null)
      );

      if (hasAllPacsData) {
        return; // PACS-Daten bereits vollständig geladen
      }

      console.log('Loading PACS data for', accessionNumbers.length, 'accession numbers and', pacsColumns.length, 'columns');
      fetchPacsData(accessionNumbers, pacsColumns).then(pacsDataArr => {
        console.log('PACS data loaded:', pacsDataArr.length, 'entries');
        const pacsDataMap = {};
        pacsDataArr.forEach(entry => {
          if (entry.accessionNumber) pacsDataMap[entry.accessionNumber] = entry;
        });
        setTeamExams(exams =>
          exams.map(exam => {
            const pacs = pacsDataMap[exam.accession_number];
            return pacs ? { ...exam, ...pacs } : exam;
          })
        );
      });
    }
  }, [teamExams.length, userColumns.length]); // Nur bei Änderung der Anzahl, nicht der Inhalte

  // Suche und Filterung
  useEffect(() => {
    const withoutApproved = teamExams.filter(exam => exam.report_status !== 'APPROVED');
    setFilteredExams(applyToRows(withoutApproved, searchFields));
  }, [teamExams, applyToRows, searchFields]);

  // DataGrid-kompatible Columns (same as MyTasks)
  const dataGridColumns = useMemo(() => {
    
    const rawFields = columnOrder.length > 0 
      ? columnOrder 
      : userColumns
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(col => col.field);
    const seenFields = new Set();
    const orderedFields = rawFields.filter((field) => {
      if (!field || seenFields.has(field)) return false;
      seenFields.add(field);
      return true;
    });
    const cols = orderedFields.map(field => {
      const col = (userColumns.find(c => c.field === field) || { field });
      const isDateField = DATE_FIELDS.includes(field);
      const isDateTimeField = DATETIME_FIELDS.includes(field);
      
      
      const column = {
        field,
        headerName: t(`taskColumns.${field}`, field),
        width: columnWidths[field] || col.width || 150,
        flex: 0, // Kein flex - feste Breite
        sortable: true,
        hide: columnVisibilityModel[field] === false,
        disableColumnMenu: true,
        ...(isDateField || isDateTimeField ? {
          renderCell: (params) => {
            if (!params.value) return '';
            
            try {
              // Fallback wenn Config noch nicht geladen ist
              if (!formatConfigLoaded) {
                const date = new Date(params.value);
                if (isNaN(date.getTime())) return params.value;
                
                if (field === 'birth_date') {
                  const today = new Date();
                  let age = today.getFullYear() - date.getFullYear();
                  const monthDiff = today.getMonth() - date.getMonth();
                  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
                    age--;
                  }
                  return `${date.toLocaleDateString('de-DE')} (${age} J.)`;
                } else if (isDateField) {
                  return date.toLocaleDateString('de-DE');
                } else {
                  return date.toLocaleString('de-DE');
                }
              }
              
              // Verwende konfigurierte Formatierung
              if (field === 'birth_date') {
                return formatDateWithAgeSync(params.value);
              } else if (isDateField) {
                return formatDateSync(params.value);
              } else {
                return formatDateTimeSync(params.value);
              }
            } catch (error) {
              console.warn(`Date formatting error for ${field}:`, error, 'Value:', params.value);
              return params.value || '';
            }
          }
        } : {}),
      };
      
      // Spezielle Behandlung für report_status Spalte
      if (field === 'report_status') {
        column.renderCell = (params) => {
          const status = params.value || 'NEW';
          const statusText = t(`reportStatus.${status}`, status);
          const cssClass = getStatusCssClass(status);
          
          return React.createElement('span', {
            className: `chip-Befundstatus ${cssClass}`
          }, statusText);
        };
      }

      // Versicherungsstatus / Dringlichkeit: als Chip mit Icon + Text (analog Report Editor)
      if (field === 'insurance_status') {
        column.renderCell = (params) => (
          params.value ? <InsuranceStatusChip value={params.value} /> : ''
        );
      }
      if (field === 'emergency_status') {
        column.renderCell = (params) => (
          params.value ? <EmergencyStatusChip value={params.value} /> : ''
        );
      }
      
      return column;
    });
    return cols;
  }, [columnOrder, userColumns, columnWidths, columnVisibilityModel, t]);

  // Zeilen für DataGrid
  const rows = useMemo(() => {
    return filteredExams.map((exam, index) => ({
      id: exam.id || index,
      ...exam
    }));
  }, [filteredExams]);

  const isColumnVisible = (field) => {
    if (!taskSettings?.columns) return false;
    const col = taskSettings.columns.find(c => c.field === field);
    return col?.visible || false;
  };

  // DataGrid-Events (same as MyTasks)
  const handleColumnVisibilityModelChange = (newModel) => {
    setColumnVisibilityModel(newModel);

    // Alle erlaubten Spalten (nicht auf Blacklist)
    const allFields = columns.filter(col => !EXCLUDED_COLUMNS.includes(col));

    // Bestimme, welche Spalten neu hinzugefügt wurden
    const currentVisibleFields = userColumns.filter(col => col.visible).map(col => col.field);
    const newVisibleFields = allFields.filter(field => newModel[field] === true);
    const newlyAddedFields = newVisibleFields.filter(field => !currentVisibleFields.includes(field));

    // Baue neue Spaltenliste: Behalte bestehende Reihenfolge bei, füge neue am Ende hinzu
    const newSettingsColumns = [];
    
    // 1. Füge alle bestehenden sichtbaren Spalten in ihrer aktuellen Reihenfolge hinzu
    userColumns.forEach(existingCol => {
      if (newModel[existingCol.field] === true) {
        newSettingsColumns.push({
          ...existingCol,
          visible: true
        });
      }
    });
    
    // 2. Füge neu hinzugefügte Spalten am Ende hinzu
    newlyAddedFields.forEach(field => {
      const existing = userColumns.find(c => c.field === field);
      const maxOrder = newSettingsColumns.length > 0 ? Math.max(...newSettingsColumns.map(col => col.order)) : -1;
      newSettingsColumns.push({
        field,
        width: existing?.width || 150,
        order: maxOrder + 1,
        visible: true
      });
    });

    const newSettings = { 
      ...taskSettings,
      columns: newSettingsColumns,
      filters: {
        modality: modalityFilter,
        status: statusFilter,
        studyDate: studyDateFilter
      },
      savedFilters: savedFilters,
      defaultFilterId: defaultFilterId
    };
    saveTaskSettings(newSettings);
  };

  const handleColumnOrderChange = (params) => {
    // params ist ein Array der Felder in neuer Reihenfolge
    if (!Array.isArray(params)) {
      console.error('handleColumnOrderChange: params ist kein Array:', params);
      return;
    }
    
    setColumnOrder(params);
    const newSettings = {
      ...taskSettings,
      columns: params.map((field, idx) => {
        const orig = userColumns.find(c => c.field === field) || { field };
        return { 
          ...orig, 
          order: idx,
          visible: columnVisibilityModel[field] !== false
        };
      }),
      filters: {
        modality: modalityFilter,
        status: statusFilter,
        studyDate: studyDateFilter
      },
      savedFilters: savedFilters,
      defaultFilterId: defaultFilterId
    };
    
    // Sofort speichern (nicht debounced)
    saveTaskSettings(newSettings);
  };

  const handleHeaderReorder = (fromField, toField) => {
    const current = (columnOrder.length > 0
      ? columnOrder
      : userColumns.map((col) => col.field)
    ).filter(Boolean);
    const next = reorderColumnFields(current, fromField, toField);
    if (next) {
      handleColumnOrderChange(next);
    }
  };

  const dataGridColumnsWithDnd = useMemo(() => (
    dataGridColumns.map((col) => ({
      ...col,
      renderHeader: (params) => (
        <DraggableColumnHeader field={params.field} onReorder={handleHeaderReorder}>
          {params.colDef.headerName}
        </DraggableColumnHeader>
      )
    }))
  ), [dataGridColumns]);

  const handleColumnOrderSave = (newOrder) => {
    // Validiere newOrder - es sollte ein Array von Spaltenobjekten sein
    if (!Array.isArray(newOrder)) {
      console.error('handleColumnOrderSave: newOrder ist kein Array:', newOrder);
      return;
    }
    
    // Verwende die order property aus den Spaltenobjekten, falls vorhanden
    const sortedOrder = [...newOrder].sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : 0;
      const orderB = b.order !== undefined ? b.order : 0;
      return orderA - orderB;
    });
    
    const fields = sortedOrder.map(col => col.field);
    handleColumnOrderChange(fields);
    setShowColumnOrderDialog(false);
  };

  const handleColumnWidthChange = (params) => {
    // params: { field, width }
    const fieldName = params.field || params.colDef?.field;
    const newWidths = { ...columnWidths, [fieldName]: params.width };
    setColumnWidths(newWidths);
    
    // Aktualisiere userColumns mit der neuen Breite
    const updatedColumns = userColumns.map(col =>
      col.field === fieldName ? { ...col, width: params.width } : col
    );
    
    const newSettings = {
      ...taskSettings,
      columns: updatedColumns,
      filters: {
        modality: modalityFilter,
        status: statusFilter,
        studyDate: studyDateFilter
      },
      savedFilters: savedFilters,
      defaultFilterId: defaultFilterId
    };
    
    // Spaltenbreiten sofort speichern (nicht debounced)
    saveTaskSettings(newSettings);
  };

  const handleRowDoubleClick = async (params) => {
    console.log('🔍 DEBUG: TeamTasks handleRowDoubleClick GESTARTET');
    console.log('🔍 DEBUG: params.row =', params.row);
    
    // Kind-Einträge von kombinierten Befunden nicht öffnen
    if (params.row._isGroupChild) {
      console.log('🔍 DEBUG: Kind-Eintrag eines kombinierten Befundes - nicht öffnen');
      showWarning(t('teamTasks.combinedReport.openParentWarning'), 'INFO-TEAMTASKS-001');
      return;
    }
    
    // Gruppen-Eintrag für kombinierten Befund
    if (params.row._isGroupParent) {
      console.log('🔍 DEBUG: Gruppen-Eintrag für kombinierten Befund - navigiere zu kombiniertem Report');
      console.log('🔍 DEBUG: Kombinierter Report ID:', params.row._combinedReportId);
      
      try {
        // Setze current_reader für alle NewReports im kombinierten Befund
        // Die erste echte ID in _groupChildren verwenden
        const firstChildId = params.row._groupChildren?.[0];
        if (firstChildId) {
          const response = await fetch(createApiUrl(`reports/${firstChildId}/assign-current-reader/${currentUser.id}`), {
            method: 'POST',
            headers: getAuthHeaders()
          });
          console.log('🔍 DEBUG: Assign Response Status:', response.status);
        }
        
        // Navigiere zum kombinierten Report über die report_id
        navigate(`/report/combined/${params.row._combinedReportId}`);
      } catch (error) {
        console.error('Fehler beim Zuweisen des Reports:', error);
        // Trotzdem navigieren
        navigate(`/report/combined/${params.row._combinedReportId}`);
      }
      return;
    }
    
    console.log('🔍 DEBUG: Report ID (reports.id) =', params.row.id);
    console.log('🔍 DEBUG: New Exam ID (new_exams.id) =', params.row.new_exam_id);
    console.log('🔍 DEBUG: Accession Number =', params.row.accession_number);
    console.log('🔍 DEBUG: Study Description =', params.row.study_description);
    console.log('🔍 DEBUG: Modality =', params.row.modality);
    console.log('🔍 DEBUG: Patient Name =', params.row.patient_name);
    console.log('🔍 DEBUG: Current Reader =', params.row.current_reader_name);
    
    try {
      // Setze current_reader für den Report
      console.log('🔍 DEBUG: Sende Assign Request für Report ID:', params.row.id, 'zu User:', currentUser.id);
      const response = await fetch(createApiUrl(`reports/${params.row.id}/assign-current-reader/${currentUser.id}`), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      console.log('🔍 DEBUG: Assign Response Status:', response.status);
      
      if (response.ok) {
        // Navigiere zum ReportEditor mit new_exam_id (nicht reports.id!)
        console.log('🔍 DEBUG: Navigiere zu /report/' + params.row.new_exam_id + ' (new_exam_id, nicht reports.id!)');
        navigate(`/report/${params.row.new_exam_id}`);
      } else {
        console.error('Fehler beim Zuweisen des Reports:', response.status, response.statusText);
        showError(t('teamTasks.error.assigningReport'), 'ERR-TEAMTASKS-008');
        // Trotzdem zum ReportEditor navigieren (falls der Report bereits zugewiesen war)
        console.log('🔍 DEBUG: Navigiere zu /report/' + params.row.new_exam_id + ' (trotz Fehler)');
        navigate(`/report/${params.row.new_exam_id}`);
      }
    } catch (error) {
      console.error('Fehler beim Zuweisen des Reports:', error);
      showError(t('teamTasks.error.networkAssigningReport'), 'ERR-TEAMTASKS-009');
      // Trotzdem zum ReportEditor navigieren
      console.log('🔍 DEBUG: Navigiere zu /report/' + params.row.new_exam_id + ' (nach Exception)');
      navigate(`/report/${params.row.new_exam_id}`);
    }
  };

  // ============================================================================
  // MULTI-SELECTION UND ZUSAMMENGEFASSTE BEFUNDE
  // ============================================================================

  // Kontextmenü öffnen bei Rechtsklick auf eine Zeile
  const handleContextMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Row-ID aus dem Event extrahieren
    const rowElement = event.currentTarget;
    const rowIdStr = rowElement?.getAttribute('data-id');
    // Konvertiere zu Number, falls die IDs als Numbers verwendet werden
    const rowId = rowIdStr ? (isNaN(Number(rowIdStr)) ? rowIdStr : Number(rowIdStr)) : null;
    
    // Sicherstellen, dass selectionModel ein Array ist
    const currentSelection = Array.isArray(selectionModel) ? selectionModel : [];
    
    // Wenn die angeklickte Zeile nicht in der Auswahl ist, zur Auswahl hinzufügen
    if (rowId !== null && !currentSelection.includes(rowId)) {
      // Wenn STRG/CMD gehalten wird, zur Auswahl hinzufügen
      if (event.ctrlKey || event.metaKey) {
        setSelectionModel([...currentSelection, rowId]);
      } else {
        // Sonst nur diese Zeile auswählen
        setSelectionModel([rowId]);
      }
    }
    
    // Kontextmenü öffnen (zeigt Optionen basierend auf Anzahl der ausgewählten Zeilen)
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  // Kontextmenü schließen
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Row-Klick Handler für STRG+Klick Multi-Selection
  const handleRowClick = (params, event) => {
    const rowId = params.id;
    const currentSelection = Array.isArray(selectionModel) ? selectionModel : [];
    
    if (event.ctrlKey || event.metaKey) {
      // STRG/CMD gedrückt - Toggle Selection
      if (currentSelection.includes(rowId)) {
        // Aus Selection entfernen
        setSelectionModel(currentSelection.filter(id => id !== rowId));
      } else {
        // Zur Selection hinzufügen
        setSelectionModel([...currentSelection, rowId]);
      }
    } else if (event.shiftKey && currentSelection.length > 0) {
      // Shift gedrückt - Bereich auswählen
      const lastSelectedId = currentSelection[currentSelection.length - 1];
      const lastIndex = rows.findIndex(row => row.id === lastSelectedId);
      const currentIndex = rows.findIndex(row => row.id === rowId);
      
      if (lastIndex !== -1 && currentIndex !== -1) {
        const startIndex = Math.min(lastIndex, currentIndex);
        const endIndex = Math.max(lastIndex, currentIndex);
        const rangeIds = rows.slice(startIndex, endIndex + 1).map(row => row.id);
        // Merge mit bestehender Selection
        const newSelection = [...new Set([...currentSelection, ...rangeIds])];
        setSelectionModel(newSelection);
      }
    } else {
      // Normaler Klick - nur diese Zeile auswählen
      setSelectionModel([rowId]);
    }
  };

  const handleDataGridRowClick = (params, event) => {
    if (embedAsWidget && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      if (lockedReports[params.id]) {
        return;
      }
      handleRowDoubleClick(params, event);
      return;
    }
    handleRowClick(params, event);
  };

  // "Zusammen befunden" Handler
  const handleCombineReports = async () => {
    handleContextMenuClose();
    
    // Sicherstellen, dass selectionModel ein Array ist
    const currentSelection = Array.isArray(selectionModel) ? selectionModel : [];
    
    if (currentSelection.length < 2) {
      showWarning(t('teamTasks.combineReports.minSelection', 'Bitte wählen Sie mindestens 2 Untersuchungen aus.'));
      return;
    }

    // Hole die ausgewählten Zeilen
    const selectedRows = filteredExams.filter(row => currentSelection.includes(row.id));
    
    // Prüfen ob alle zur gleichen Patient-ID gehören
    const patientIds = [...new Set(selectedRows.map(row => row.patient_id))];
    if (patientIds.length > 1) {
      showError(t('teamTasks.combineReports.differentPatients', 'Die ausgewählten Studien gehören zu unterschiedlichen Patienten.'));
      return;
    }

    // Sammle new_exam_ids
    const newExamIds = selectedRows.map(row => row.new_exam_id);
    
    setCombineValidating(true);
    
    try {
      // Backend-Validierung
      const response = await fetch(createApiUrl('reports/validate-combine'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ new_exam_ids: newExamIds })
      });

      const result = await response.json();

      if (!result.valid) {
        showError(result.error);
        setCombineValidating(false);
        return;
      }

      // Validierung erfolgreich - navigiere zum ReportEditor mit allen exam IDs
      const examIdsParam = newExamIds.join(',');
      console.log('🔗 Navigiere zu kombiniertem Befund mit exam IDs:', examIdsParam);
      navigate(`/report/combined?examIds=${examIdsParam}`);
      
    } catch (error) {
      console.error('Fehler bei der Validierung:', error);
      showError(t('teamTasks.combineReports.validationError', 'Fehler bei der Validierung. Bitte versuchen Sie es erneut.'));
    } finally {
      setCombineValidating(false);
    }
  };

  // Selection-Änderung Handler
  const handleSelectionModelChange = (newSelectionModel) => {
    // MUI DataGrid v8 kann verschiedene Formate liefern - immer zu Array konvertieren
    const selectionArray = Array.isArray(newSelectionModel) 
      ? newSelectionModel 
      : Array.from(newSelectionModel || []);
    setSelectionModel(selectionArray);
  };

  if (!userChecked) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Typography>Lade Benutzerdaten...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: embedAsWidget ? '100%' : undefined,
        ...(!embedAsWidget
          ? { px: { xs: 2, sm: 3 }, boxSizing: 'border-box' }
          : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' })
      }}
    >
    <Box
      sx={{
        width: '100%',
        ...(!embedAsWidget
          ? { zoom: TEAM_TASKS_PAGE_CONTENT_ZOOM }
          : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' })
      }}
    >
      <Paper
        sx={{
          width: '100%',
          mt: 0,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
          ...(embedAsWidget
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }
            : {
                height: `calc((100vh - 200px) / ${TEAM_TASKS_PAGE_CONTENT_ZOOM})`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              })
        }}
      >
        <Box
          sx={{
            p: embedAsWidget ? 1.5 : 3,
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {!embedAsWidget && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <AccessTime sx={{ fontSize: 20 }} />
              {teamExams.length === 1
                ? t('teamTasks.oneReport', '1 offener Befund')
                : t('teamTasks.reportCount', `${teamExams.length} offene Befunde`, { count: teamExams.length })
              }
            </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
          <TaskSearchBar
            searchChips={searchChips}
            placeholder={t('taskFilters.placeholder')}
            onAddSearchChip={addSearchChip}
            onRemoveSearchChip={removeSearchChip}
            conditions={validConditions}
            onRemoveCondition={(conditionId) => {
              removeCondition(conditionId);
              setSelectedFilterId('');
            }}
            onOpenFilter={openFilterDialog}
            filterActive={hasActiveConditions || filterDialogOpen}
            endContent={(
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('teamTasks.filters.savedFilters', 'Gespeicherte Filter')}</InputLabel>
                <Select
                  value={selectedFilterId}
                  label={t('teamTasks.filters.savedFilters', 'Gespeicherte Filter')}
                  onChange={handleFilterSelect}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>{t('teamTasks.filters.noSelection', 'Keine Auswahl')}</em>;
                    }
                    const filter = savedFilters.find(f => f.id === selected);
                    return filter ? filter.name : '';
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                        width: 350,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: 'var(--rd-primary)'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('teamTasks.filters.noSelection', 'Keine Auswahl')}</em>
                  </MenuItem>
                  {savedFilters.map((filter) => (
                    <MenuItem key={filter.id} value={filter.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            icon={<StarBorder />}
                            checkedIcon={<Star />}
                            checked={defaultFilterId === filter.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDefaultFilter(filter.id);
                            }}
                            sx={{ 
                              color: 'orange', 
                              '&.Mui-checked': { color: 'orange' },
                              mr: 1
                            }}
                          />
                          <ListItemText primary={filter.name} />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFilter(filter.id);
                          }}
                          sx={{ 
                            ml: 0.5,
                            color: 'error.main',
                            '&:hover': { 
                              backgroundColor: 'error.light',
                              color: 'error.dark'
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            afterFilterContent={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {SHOW_COLUMN_ORDER_DIALOG && (
                  <Tooltip title={t('common.columnOrder', 'Spaltenreihenfolge ändern')}>
                    <IconButton
                      onClick={() => setShowColumnOrderDialog(true)}
                      sx={{ color: 'var(--rd-primary)' }}
                    >
                      <DragIndicator />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t('common.manageColumns', 'Spalten verwalten')}>
                  <IconButton
                    onClick={() => {
                      if (!showColumnSelector) {
                        setDialogColumnVisibilityModel({...columnVisibilityModel});
                      }
                      setShowColumnSelector(!showColumnSelector);
                    }}
                    sx={{ color: 'var(--rd-primary)' }}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
          </Box>
          <TaskFilterDialog
            open={filterDialogOpen}
            anchorEl={filterAnchorEl}
            onClose={closeFilterDialog}
            onApply={(nextConditions) => {
              setConditions(nextConditions);
              setSelectedFilterId('');
            }}
            onSave={handleSaveFilter}
            conditions={conditions}
            columns={filterDialogColumns}
            availableStatuses={availableStatuses}
          />
          </Box>
          )}

          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={
                embedAsWidget
                  ? {
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      zoom: WIDGET_TABLE_ZOOM,
                      width: '100%'
                    }
                  : {
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }
              }
            >
            <DataGrid
              rows={rows}
              columns={dataGridColumnsWithDnd}
              loading={loading}
              disableRowSelectionOnClick={true}
              onRowClick={handleDataGridRowClick}
              onRowDoubleClick={(params, event) => {
                // Blockiere Click wenn Report gelockt ist
                if (lockedReports[params.id]) {
                  console.log(`🚫 Click blockiert - Report ${params.id} ist gelockt von`, lockedReports[params.id].user_name);
                  event.defaultMuiPrevented = true;
                  return;
                }
                handleRowDoubleClick(params, event);
              }}
              hideFooter={true}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
              onColumnOrderChange={(params) => {
                const newOrder = Array.isArray(params) ? params : params.columnFields;
                handleColumnOrderChange(newOrder);
              }}
              onColumnWidthChange={handleColumnWidthChange}
              sx={{
                ...(!embedAsWidget && { flex: 1 }),
                ...(embedAsWidget
                  ? {
                      flex: 1,
                      minHeight: 0,
                      width: '100%',
                      overflow: 'hidden'
                    }
                  : {}),
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : 'var(--rd-bg-paper)',
                borderRadius: 2,
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : 'var(--rd-bg-subtle)',
                  color: (theme) => theme.palette.mode === 'dark' ? theme.palette.text.primary : 'var(--rd-text-primary)',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  borderBottom: (theme) => theme.palette.mode === 'dark' ? '1px solid var(--rd-divider)' : '1px solid var(--rd-divider)',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                  lineHeight: '1.2',
                  wordBreak: 'break-word',
                  textAlign: 'center',
                },
                '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent) !important',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--rd-primary) 4%, transparent)',
                },
                '& .MuiDataGrid-row.row-locked': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
                  opacity: 0.6,
                  cursor: 'not-allowed',
                },
                '& .MuiDataGrid-row.row-locked:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.15) !important',
                },
                // Kombinierte Befunde: Gruppen-Eltern-Eintrag hervorheben
                '& .MuiDataGrid-row.row-group-parent': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 15%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 8%, transparent)',
                  fontWeight: 'bold',
                  borderLeft: '3px solid var(--rd-primary)',
                },
                '& .MuiDataGrid-row.row-group-parent:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 25%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 15%, transparent)',
                },
                // Kombinierte Befunde: Kind-Einträge einrücken und ausgegraut
                '& .MuiDataGrid-row.row-group-child': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(100, 100, 100, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                  opacity: 0.7,
                  cursor: 'default !important',
                  borderLeft: '3px solid transparent',
                  marginLeft: '20px',
                },
                '& .MuiDataGrid-row.row-group-child:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(100, 100, 100, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                  cursor: 'default !important',
                },
                '& .MuiDataGrid-row.row-group-child .MuiDataGrid-cell:first-of-type': {
                  paddingLeft: '28px',
                },
                '& .MuiDataGrid-cell': {
                  color: (theme) => theme.palette.mode === 'dark' ? theme.palette.text.primary : 'var(--rd-text-primary)',
                  fontSize: '0.98rem',
                  borderBottom: (theme) => theme.palette.mode === 'dark' ? '1px solid var(--rd-divider)' : '1px solid var(--rd-divider)',
                },
                '& .MuiDataGrid-columnHeader': {
                  cursor: 'grab',
                },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-footerContainer': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : 'var(--rd-bg-subtle)',
                  borderTop: (theme) => theme.palette.mode === 'dark' ? '1px solid var(--rd-divider)' : '1px solid var(--rd-divider)',
                },
                '& .MuiDataGrid-footer': {
                  borderTop: (theme) => theme.palette.mode === 'dark' ? '1px solid var(--rd-divider)' : '1px solid var(--rd-divider)',
                },
              }}
              headerHeight={56}
              disableColumnReorder={false}
              disableColumnResize={false}
              columnBufferPx={800}
              getRowHeight={() => 52}
              disableColumnMenu={true}
              isRowSelectable={(params) => {
                // Kind-Einträge von kombinierten Befunden nicht auswählbar
                if (params.row._isGroupChild) return false;
                return !lockedReports[params.id];
              }}
              getRowClassName={(params) => {
                const classes = [];
                if (lockedReports[params.id]) classes.push('row-locked');
                const currentSelection = Array.isArray(selectionModel) ? selectionModel : [];
                if (currentSelection.includes(params.id)) classes.push('Mui-selected');
                // Kombinierte Befunde: Spezielle Klassen für Gruppierung
                if (params.row._isGroupParent) classes.push('row-group-parent');
                if (params.row._isGroupChild) classes.push('row-group-child');
                return classes.join(' ');
              }}
              slotProps={{
                row: {
                  onContextMenu: handleContextMenu
                },
                cell: {
                  onMouseEnter: (event) => {
                    const rowId = event.currentTarget.parentElement.getAttribute('data-id');
                    const lockInfo = lockedReports[rowId];
                    if (lockInfo) {
                      event.currentTarget.setAttribute('title', t('dashboard.cards.teamTasks.currentlyOpenedBy', {
                        title: lockInfo.user_name?.split(' ')[0] || '',
                        firstname: lockInfo.user_name?.split(' ')[1] || '',
                        lastname: lockInfo.user_name?.split(' ')[2] || lockInfo.user_name
                      }));
                    }
                  },
                  onMouseLeave: (event) => {
                    event.currentTarget.removeAttribute('title');
                  }
                }
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: 2,
                    py: 4
                  }}>
                    <Typography variant="body1" color="text.secondary">
                      Keine verfügbaren Aufgaben gefunden.
                    </Typography>
                  </Box>
                )
              }}
            />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Spaltenverwaltungs-Dialog (same as MyTasks) */}
      <Dialog
        open={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '600px', // Feste Höhe
            maxHeight: '80vh' // Maximal 80% der Bildschirmhöhe
          }
        }}
      >
        <DialogTitle>
          {t('common.manageColumns', 'Spalten verwalten')}
        </DialogTitle>
        <DialogContent sx={{ height: '450px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Suchfeld für Spalten */}
          <TextField
            fullWidth
            size="small"
            placeholder={t('common.searchColumns', 'Spalten suchen...')}
            value={columnSearchTerm}
            onChange={(e) => setColumnSearchTerm(e.target.value)}
            sx={{ mb: 2, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'var(--rd-primary)' }} />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ 
            display: 'inline-grid',
            gridTemplateColumns: 'repeat(3, minmax(220px, max-content))',
            gridAutoRows: '40px', // Feste Zeilenhöhe für kompakte Anordnung
            gap: 2, 
            mt: 1,
            overflow: 'auto',
            flex: 1,
            justifyContent: 'start',
            justifyItems: 'start',
            alignItems: 'start',
          }}>
            {columns
              .filter(col => !EXCLUDED_COLUMNS.includes(col))
              .filter(col => {
                if (!columnSearchTerm) return true;
                const searchLower = columnSearchTerm.toLowerCase();
                const fieldName = col.toLowerCase();
                const translatedName = t(`taskColumns.${col}`, col).toLowerCase();
                return fieldName.includes(searchLower) || translatedName.includes(searchLower);
              })
              // Sortierung nach Übersetzung (Label)
              .sort((a, b) => {
                const labelA = t(`taskColumns.${a}`, a).toLowerCase();
                const labelB = t(`taskColumns.${b}`, b).toLowerCase();
                return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
              })
              .map((field) => {
                const colObj = userColumns.find(c => c.field === field) || { field };
                return (
                  <FormControlLabel
                    key={field}
                    control={
                      <Checkbox
                        checked={dialogColumnVisibilityModel[field] === true}
                        onChange={(e) => {
                          const newModel = {
                            ...dialogColumnVisibilityModel,
                            [field]: e.target.checked
                          };
                          setDialogColumnVisibilityModel(newModel);
                        }}
                      />
                    }
                    label={t(`taskColumns.${field}`, field)}
                    sx={{ m: 0 }}
                  />
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              // Änderungen verwerfen: dialogColumnVisibilityModel zurücksetzen
              setDialogColumnVisibilityModel({...columnVisibilityModel});
              setShowColumnSelector(false);
              setColumnSearchTerm(''); // Suchfeld zurücksetzen
            }}
            className="button-cancel"
          >
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button 
            onClick={() => {
              handleColumnVisibilityModelChange(dialogColumnVisibilityModel);
              setShowColumnSelector(false);
              setColumnSearchTerm(''); // Suchfeld zurücksetzen
            }}
            className="button"
          >
            {t('common.apply', 'Anwenden')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kontextmenü für "Zusammen befunden" */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem 
          onClick={handleCombineReports}
          disabled={combineValidating || (Array.isArray(selectionModel) ? selectionModel.length : 0) < 2}
        >
          <ListItemIcon>
            <MergeType fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {combineValidating 
              ? t('teamTasks.combineReports.validating', 'Validiere...')
              : t('teamTasks.combineReports.action', 'Zusammen befunden')
            }
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Spaltenreihenfolge-Dialog (same as MyTasks) */}
      <ColumnOrderDialog
        open={showColumnOrderDialog}
        onClose={() => setShowColumnOrderDialog(false)}
        columns={(() => {
          // Verwende columnOrder, falls vorhanden, um die Reihenfolge zu bestimmen
          let filteredColumns;
          if (columnOrder.length > 0) {
            // Erstelle Spalten in der richtigen Reihenfolge
            filteredColumns = columnOrder
              .map(field => userColumns.find(col => col.field === field))
              .filter(col => col && columnVisibilityModel[col.field] !== false);
          } else {
            // Fallback: Verwende userColumns direkt
            filteredColumns = userColumns.filter(col => columnVisibilityModel[col.field] !== false);
          }
          return filteredColumns;
        })()}
        onSave={handleColumnOrderSave}
      />
    </Box>
    </Box>
  );
};

export default TeamTasks; 